import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  ArrowUpRight, ArrowDownRight, Gift, Clock, ChevronLeft, ChevronRight,
  Download, Calendar, Mail, Settings, FileSpreadsheet, Loader2, AlertTriangle, Landmark, CheckCircle, MoreVertical, RefreshCw, Banknote,
  ShieldAlert, ShieldCheck, XCircle, Plus, Pencil, Ban, Search, ChevronsUpDown, Check, Users
} from 'lucide-react';
import { BackButton, PageSkeleton, StatusBadge, TransactionIcon, getTransactionLabel } from '../../components/common';

export const AdminTransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);
  const [bankCsvLoading, setBankCsvLoading] = useState(false);
  const [markProcessedLoading, setMarkProcessedLoading] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [reportEmails, setReportEmails] = useState(['', '', '']);
  const [savingEmails, setSavingEmails] = useState(false);
  const limit = 20;

  // Admin Adjustments State
  const [activeTab, setActiveTab] = useState('transactions');
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentsTotal, setAdjustmentsTotal] = useState(0);
  const [adjustmentsPage, setAdjustmentsPage] = useState(1);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentStatusFilter, setAdjustmentStatusFilter] = useState('');
  
  // Create Adjustment Modal
  const [createAdjustmentOpen, setCreateAdjustmentOpen] = useState(false);
  const [createAdjustmentLoading, setCreateAdjustmentLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('debit');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentRemark, setAdjustmentRemark] = useState('');
  
  // Edit Adjustment Modal
  const [editAdjustmentOpen, setEditAdjustmentOpen] = useState(false);
  const [editAdjustmentLoading, setEditAdjustmentLoading] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editRemark, setEditRemark] = useState('');
  
  // Void Adjustment Modal
  const [voidAdjustmentOpen, setVoidAdjustmentOpen] = useState(false);
  const [voidAdjustmentLoading, setVoidAdjustmentLoading] = useState(false);
  const [voidingAdjustment, setVoidingAdjustment] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  // Load email settings on mount
  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const res = await adminAPI.getDailyReportSettings();
        if (res.data?.emails) {
          const emails = res.data.emails;
          setReportEmails([emails[0] || '', emails[1] || '', emails[2] || '']);
        }
      } catch (error) {
        // Settings might not exist yet
      }
    };
    loadEmailSettings();
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getTransactions(
        page * limit, 
        limit, 
        typeFilter, 
        statusFilter,
        startDate || undefined,
        endDate || undefined
      );
      setTransactions(response.data.transactions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, statusFilter, startDate, endDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Fetch adjustments
  const fetchAdjustments = useCallback(async () => {
    setAdjustmentsLoading(true);
    try {
      const response = await adminAPI.getAdjustments('', adjustmentStatusFilter, adjustmentsPage, 20);
      setAdjustments(response.data.adjustments || []);
      setAdjustmentsTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load adjustments');
    } finally {
      setAdjustmentsLoading(false);
    }
  }, [adjustmentsPage, adjustmentStatusFilter]);

  useEffect(() => {
    if (activeTab === 'adjustments') {
      fetchAdjustments();
    }
  }, [activeTab, fetchAdjustments]);

  // Fetch users for dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers(0, 500);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users based on search term
  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.phone?.includes(userSearchTerm)
  );

  // Create adjustment handler
  const handleCreateAdjustment = async () => {
    if (!selectedUserId || !adjustmentAmount || !adjustmentRemark) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (adjustmentRemark.length < 5) {
      toast.error('Remark must be at least 5 characters');
      return;
    }
    
    setCreateAdjustmentLoading(true);
    try {
      const response = await adminAPI.createAdjustment({
        user_id: selectedUserId,
        adjustment_type: adjustmentType,
        milli_amount: amount,
        remark: adjustmentRemark
      });
      
      toast.success(response.data.message || 'Adjustment created successfully');
      setCreateAdjustmentOpen(false);
      resetCreateForm();
      fetchAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create adjustment');
    } finally {
      setCreateAdjustmentLoading(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedUserId('');
    setSelectedUserName('');
    setAdjustmentType('debit');
    setAdjustmentAmount('');
    setAdjustmentRemark('');
    setUserSearchTerm('');
  };

  // Edit adjustment handler
  const handleEditAdjustment = async () => {
    if (!editingAdjustment) return;
    
    const changes = {};
    const amount = parseFloat(editAmount);
    
    if (editAmount && !isNaN(amount) && amount > 0 && amount !== editingAdjustment.milli_amount) {
      changes.milli_amount = amount;
    }
    
    if (editRemark && editRemark !== editingAdjustment.remark) {
      if (editRemark.length < 5) {
        toast.error('Remark must be at least 5 characters');
        return;
      }
      changes.remark = editRemark;
    }
    
    if (Object.keys(changes).length === 0) {
      toast.error('No changes to save');
      return;
    }
    
    setEditAdjustmentLoading(true);
    try {
      await adminAPI.editAdjustment(editingAdjustment.transaction_id, changes);
      toast.success('Adjustment updated successfully');
      setEditAdjustmentOpen(false);
      setEditingAdjustment(null);
      fetchAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update adjustment');
    } finally {
      setEditAdjustmentLoading(false);
    }
  };

  // Void adjustment handler
  const handleVoidAdjustment = async () => {
    if (!voidingAdjustment || !voidReason) {
      toast.error('Please provide a reason for voiding');
      return;
    }
    
    if (voidReason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    
    setVoidAdjustmentLoading(true);
    try {
      await adminAPI.voidAdjustment(voidingAdjustment.transaction_id, voidReason);
      toast.success('Adjustment voided successfully');
      setVoidAdjustmentOpen(false);
      setVoidingAdjustment(null);
      setVoidReason('');
      fetchAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void adjustment');
    } finally {
      setVoidAdjustmentLoading(false);
    }
  };

  const openEditModal = (adjustment) => {
    setEditingAdjustment(adjustment);
    setEditAmount(adjustment.milli_amount.toString());
    setEditRemark(adjustment.remark || '');
    setEditAdjustmentOpen(true);
  };

  const openVoidModal = (adjustment) => {
    setVoidingAdjustment(adjustment);
    setVoidReason('');
    setVoidAdjustmentOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getTransactionIcon = (type, status, adjustmentType = null) => {
    // Show warning icon for failed/aborted transactions
    if (status === 'failed' || status === 'aborted') {
      return <AlertTriangle className="w-5 h-5 text-red-600" />;
    }
    if (status === 'voided') {
      return <XCircle className="w-5 h-5 text-gray-400" />;
    }
    if (status === 'pending') {
      return <Clock className="w-5 h-5 text-yellow-600" />;
    }
    switch (type) {
      case 'buy': return <ArrowDownRight className="w-5 h-5 text-green-600" />;
      case 'sell': return <ArrowUpRight className="w-5 h-5 text-orange-600" />;
      case 'redeem': return <Gift className="w-5 h-5 text-purple-600" />;
      case 'admin_adjustment': 
        if (adjustmentType === 'debit') {
          return <ShieldAlert className="w-5 h-5 text-red-600" />;
        }
        return <ShieldCheck className="w-5 h-5 text-teal-600" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getTransactionLabel = (type, status, adjustmentType = null) => {
    if (status === 'failed' || status === 'aborted') return 'Failed';
    if (status === 'voided') return 'Voided';
    if (status === 'pending') return 'Pending';
    switch (type) {
      case 'buy': return 'Booked';
      case 'sell': return 'Refund';
      case 'redeem': return 'Redeemed';
      case 'admin_adjustment':
        return adjustmentType === 'debit' ? 'Admin Deduction' : 'Admin Credit';
      default: return type;
    }
  };

  const getTransactionBgColor = (type, status, adjustmentType = null) => {
    if (status === 'failed' || status === 'aborted') return 'bg-red-100';
    if (status === 'voided') return 'bg-gray-100';
    if (status === 'pending') return 'bg-yellow-100';
    switch (type) {
      case 'buy': return 'bg-green-100';
      case 'sell': return 'bg-orange-100';
      case 'redeem': return 'bg-purple-100';
      case 'admin_adjustment':
        return adjustmentType === 'debit' ? 'bg-red-50' : 'bg-teal-50';
      default: return 'bg-gray-100';
    }
  };

  const getTransactionTextColor = (type, status, adjustmentType = null) => {
    if (status === 'failed' || status === 'aborted') return 'text-red-600';
    if (status === 'voided') return 'text-gray-400';
    if (status === 'pending') return 'text-yellow-600';
    switch (type) {
      case 'buy': return 'text-green-600';
      case 'sell': return 'text-orange-600';
      case 'redeem': return 'text-purple-600';
      case 'admin_adjustment':
        return adjustmentType === 'debit' ? 'text-red-600' : 'text-teal-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      case 'aborted':
        return <Badge className="bg-gray-100 text-gray-700">Aborted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const downloadCSV = async () => {
    setCsvLoading(true);
    try {
      const response = await adminAPI.downloadTransactionsCSV(
        typeFilter || undefined,
        statusFilter || undefined,
        startDate || undefined,
        endDate || undefined
      );
      
      const { csv_content, filename } = response.data;
      
      // Create blob and download
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download CSV');
    } finally {
      setCsvLoading(false);
    }
  };

  const downloadBankRefundCSV = async () => {
    setBankCsvLoading(true);
    try {
      const response = await adminAPI.downloadBankRefundCSV(
        startDate || undefined,
        endDate || undefined
      );
      
      const { csv_content, filename, total_records } = response.data;
      
      if (total_records === 0) {
        toast.info('No pending refunds to process');
        return;
      }
      
      // Create blob and download
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Bank refund CSV downloaded! (${total_records} records)`);
    } catch (error) {
      toast.error('Failed to download bank refund CSV');
    } finally {
      setBankCsvLoading(false);
    }
  };

  const markRefundsProcessed = async () => {
    if (!window.confirm('Are you sure you want to mark all pending refunds as processed? This will update their status to "Completed".')) {
      return;
    }
    
    setMarkProcessedLoading(true);
    try {
      const response = await adminAPI.markRefundsProcessed(
        startDate || undefined,
        endDate || undefined
      );
      
      const { processed_count, message } = response.data;
      
      if (processed_count === 0) {
        toast.info('No pending refunds to process');
      } else {
        toast.success(message);
        // Refresh the transactions list
        fetchTransactions();
      }
    } catch (error) {
      toast.error('Failed to mark refunds as processed');
    } finally {
      setMarkProcessedLoading(false);
    }
  };

  const updateTransactionStatus = async (transactionId, newStatus) => {
    if (!window.confirm(`Are you sure you want to change the status to "${newStatus}"?`)) {
      return;
    }
    
    setUpdatingStatusId(transactionId);
    try {
      const response = await adminAPI.updateTransactionStatus(transactionId, newStatus);
      toast.success(response.data.message);
      // Refresh the transactions list
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const processFailedTransaction = async (transactionId) => {
    if (!window.confirm('This will mark the transaction as COMPLETED and credit the Millis to the user\'s balance. Are you sure the payment was received in Razorpay?')) {
      return;
    }
    
    setUpdatingStatusId(transactionId);
    try {
      const response = await adminAPI.processFailedTransaction(transactionId);
      const { milli_credited, new_balance, user_id } = response.data;
      toast.success(`Transaction processed! ${milli_credited} Millis credited to user. New balance: ${new_balance}`);
      // Refresh the transactions list
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process transaction');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const creditMillisForTransaction = async (transactionId) => {
    if (!window.confirm('This will credit the Millis to the user\'s balance for this transaction. Continue?')) {
      return;
    }
    
    setUpdatingStatusId(transactionId);
    try {
      const response = await adminAPI.creditMillisForTransaction(transactionId);
      const { milli_credited, new_balance, user_name } = response.data;
      toast.success(`${milli_credited} Millis credited to ${user_name}! New balance: ${new_balance}`);
      // Refresh the transactions list
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit millis');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const saveEmailSettings = async () => {
    const validEmails = reportEmails.filter(e => e.trim() !== '');
    
    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of validEmails) {
      if (!emailRegex.test(email)) {
        toast.error(`Invalid email: ${email}`);
        return;
      }
    }
    
    setSavingEmails(true);
    try {
      await adminAPI.saveDailyReportSettings(validEmails);
      toast.success('Daily report settings saved!');
      setEmailSettingsOpen(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingEmails(false);
    }
  };

  const applyDatePreset = (preset) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    switch (preset) {
      case 'today': {
        const today = formatLocalDate(now);
        setStartDate(today);
        setEndDate(today);
        break;
      }
      case 'this_week': {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        setStartDate(formatLocalDate(startOfWeek));
        setEndDate(formatLocalDate(now));
        break;
      }
      case 'this_month': {
        const startOfMonth = new Date(currentYear, currentMonth, 1);
        setStartDate(formatLocalDate(startOfMonth));
        setEndDate(formatLocalDate(now));
        break;
      }
      case 'this_fy': {
        // Financial year: April 1 to March 31
        // If current month is Jan-Mar (0-2), FY started last year
        const fyStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        const startOfFY = new Date(fyStartYear, 3, 1); // April 1
        setStartDate(formatLocalDate(startOfFY));
        setEndDate(formatLocalDate(now));
        break;
      }
      case 'clear': {
        setStartDate('');
        setEndDate('');
        break;
      }
      default:
        break;
    }
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-transactions-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">All Transactions</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Bank Refund CSV */}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadBankRefundCSV}
              disabled={bankCsvLoading}
              className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="bank-refund-csv-btn"
            >
              {bankCsvLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Landmark className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Bank Refund</span>
            </Button>
            
            {/* Mark Refunds as Processed */}
            <Button
              variant="outline"
              size="sm"
              onClick={markRefundsProcessed}
              disabled={markProcessedLoading}
              className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              data-testid="mark-processed-btn"
            >
              {markProcessedLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Mark Processed</span>
            </Button>
            
            {/* CSV Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCSV}
              disabled={csvLoading}
              className="gap-2"
            >
              {csvLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Download CSV</span>
            </Button>
            
            {/* Email Settings */}
            <Dialog open={emailSettingsOpen} onOpenChange={setEmailSettingsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">Daily Report</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-navy flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Daily Report Settings
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <p className="text-sm text-muted-foreground">
                    Enter up to 3 email addresses to receive daily transaction reports.
                  </p>
                  
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="space-y-1">
                      <Label className="text-xs">Email {index + 1}</Label>
                      <Input
                        type="email"
                        placeholder={`email${index + 1}@example.com`}
                        value={reportEmails[index]}
                        onChange={(e) => {
                          const newEmails = [...reportEmails];
                          newEmails[index] = e.target.value;
                          setReportEmails(newEmails);
                        }}
                      />
                    </div>
                  ))}
                  
                  <Button
                    onClick={saveEmailSettings}
                    disabled={savingEmails}
                    className="w-full gold-gradient text-navy font-semibold"
                  >
                    {savingEmails ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Settings className="w-4 h-4 mr-2" />
                    )}
                    Save Settings
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="transactions">All Transactions</TabsTrigger>
            <TabsTrigger value="adjustments">Admin Adjustments</TabsTrigger>
          </TabsList>

          {/* All Transactions Tab */}
          <TabsContent value="transactions">
            {/* Date Filters */}
            <Card className="mb-4 border-gold/20">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                      className="h-9"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                      className="h-9"
                    />
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => applyDatePreset('today')}>Today</Button>
                    <Button size="sm" variant="outline" onClick={() => applyDatePreset('this_week')}>This Week</Button>
                    <Button size="sm" variant="outline" onClick={() => applyDatePreset('this_month')}>This Month</Button>
                    <Button size="sm" variant="outline" onClick={() => applyDatePreset('this_fy')}>This FY</Button>
                    <Button size="sm" variant="ghost" onClick={() => applyDatePreset('clear')}>Clear</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Type & Status Filters */}
            <div className="flex gap-4 mb-6">
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(0); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="buy">Buy</SelectItem>
                  <SelectItem value="sell">Sell</SelectItem>
                  <SelectItem value="redeem">Redeem</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {transactions.length} of {total} transactions
                {(startDate || endDate) && (
                  <span className="ml-2 text-primary">
                    ({startDate || 'Start'} to {endDate || 'End'})
                  </span>
                )}
          </p>
        </div>

        {/* Transaction List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <Card className="border-gold/20">
            <CardContent className="py-12 text-center">
              <Clock className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No transactions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {transactions.map((txn) => (
              <Card key={txn.transaction_id} className="border-gold/20">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getTransactionBgColor(txn.type, txn.status, txn.adjustment_type)}`}>
                        {getTransactionIcon(txn.type, txn.status, txn.adjustment_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-navy">{getTransactionLabel(txn.type, txn.status, txn.adjustment_type)}</p>
                          {getStatusBadge(txn.status)}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          ID: {txn.transaction_id}
                        </p>
                        <p className="text-sm font-medium text-navy">
                          {txn.user_name || 'Unknown'} <span className="text-muted-foreground font-normal">({txn.user_phone || txn.user_id})</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTimeIST(txn.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold text-lg ${getTransactionTextColor(txn.type, txn.status, txn.adjustment_type)}`}>
                        {txn.type === 'admin_adjustment' ? (txn.adjustment_type === 'debit' ? '-' : '+') : ''}{txn.milli_amount?.toFixed(2)} Milli's
                      </p>
                      {txn.amount_inr > 0 && (
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(txn.amount_inr)}
                        </p>
                      )}
                      {/* Only show rate for non-adjustment transactions */}
                      {txn.type !== 'admin_adjustment' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Rate: {formatCurrency(txn.rate_at_time)}/M
                        </p>
                      )}
                      
                      {/* Status Update Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-2 h-7 text-xs"
                            disabled={updatingStatusId === txn.transaction_id}
                          >
                            {updatingStatusId === txn.transaction_id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <RefreshCw className="w-3 h-3 mr-1" />
                            )}
                            Change Status
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => updateTransactionStatus(txn.transaction_id, 'pending')}
                            disabled={txn.status === 'pending'}
                            className="text-yellow-600"
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            Set to Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => updateTransactionStatus(txn.transaction_id, 'completed')}
                            disabled={txn.status === 'completed'}
                            className="text-green-600"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Set to Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => updateTransactionStatus(txn.transaction_id, 'failed')}
                            disabled={txn.status === 'failed'}
                            className="text-red-600"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Set to Failed
                          </DropdownMenuItem>
                          {/* Process Failed Payment - Only for failed BUY transactions */}
                          {txn.type === 'buy' && txn.status === 'failed' && (
                            <DropdownMenuItem 
                              onClick={() => processFailedTransaction(txn.transaction_id)}
                              className="text-blue-600 font-medium border-t mt-1 pt-1"
                            >
                              <Banknote className="w-4 h-4 mr-2" />
                              Process Failed Payment
                            </DropdownMenuItem>
                          )}
                          {/* Credit Millis - For completed BUY transactions where millis weren't credited */}
                          {txn.type === 'buy' && txn.status === 'completed' && (
                            <DropdownMenuItem 
                              onClick={() => creditMillisForTransaction(txn.transaction_id)}
                              className="text-purple-600 font-medium border-t mt-1 pt-1"
                            >
                              <Banknote className="w-4 h-4 mr-2" />
                              Credit Millis to User
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
          </TabsContent>

          {/* Admin Adjustments Tab */}
          <TabsContent value="adjustments">
            {/* Header with Create Button */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-navy">Balance Adjustments</h3>
                <p className="text-sm text-muted-foreground">Create, edit, or void balance adjustments for policy violations</p>
              </div>
              <Button onClick={() => setCreateAdjustmentOpen(true)} className="gold-gradient text-navy font-semibold">
                <Plus className="w-4 h-4 mr-2" />
                New Adjustment
              </Button>
            </div>

            {/* Status Filter */}
            <div className="flex gap-4 mb-6">
              <Select value={adjustmentStatusFilter} onValueChange={(v) => { setAdjustmentStatusFilter(v === 'all' ? '' : v); setAdjustmentsPage(1); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="voided">Voided</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {adjustments.length} of {adjustmentsTotal} adjustments
              </p>
            </div>

            {/* Adjustments List */}
            {adjustmentsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            ) : adjustments.length === 0 ? (
              <Card className="border-gold/20">
                <CardContent className="py-12 text-center">
                  <ShieldAlert className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No adjustments found</p>
                  <p className="text-sm text-muted-foreground mt-1">Create a new adjustment to deduct or credit user balances</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {adjustments.map((adj) => (
                  <Card key={adj.transaction_id} className={`border-gold/20 ${adj.status === 'voided' ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            adj.status === 'voided' ? 'bg-gray-100' :
                            adj.adjustment_type === 'debit' ? 'bg-red-50' : 'bg-teal-50'
                          }`}>
                            {adj.status === 'voided' ? (
                              <XCircle className="w-5 h-5 text-gray-400" />
                            ) : adj.adjustment_type === 'debit' ? (
                              <ShieldAlert className="w-5 h-5 text-red-600" />
                            ) : (
                              <ShieldCheck className="w-5 h-5 text-teal-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-semibold ${
                                adj.status === 'voided' ? 'text-gray-400' :
                                adj.adjustment_type === 'debit' ? 'text-red-600' : 'text-teal-600'
                              }`}>
                                {adj.adjustment_type === 'debit' ? 'Deduction' : 'Credit'}
                              </p>
                              {adj.status === 'voided' && (
                                <Badge variant="outline" className="text-gray-500 border-gray-300">Voided</Badge>
                              )}
                              {adj.is_reversal && (
                                <Badge variant="outline" className="text-blue-500 border-blue-300">Reversal</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              ID: {adj.transaction_id}
                            </p>
                            <p className="text-sm font-medium text-navy">
                              {adj.user_name || 'Unknown'} <span className="text-muted-foreground font-normal">({adj.user_phone || adj.user_id})</span>
                            </p>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              <span className="font-medium">Remark:</span> {adj.remark}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              By: {adj.created_by_name} • {formatDateTimeIST(adj.created_at)}
                            </p>
                            {adj.status === 'voided' && adj.void_reason && (
                              <p className="text-xs text-red-500 mt-1">
                                <span className="font-medium">Void Reason:</span> {adj.void_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${
                            adj.status === 'voided' ? 'text-gray-400 line-through' :
                            adj.adjustment_type === 'debit' ? 'text-red-600' : 'text-teal-600'
                          }`}>
                            {adj.adjustment_type === 'debit' ? '-' : '+'}{adj.milli_amount?.toFixed(2)} Milli's
                          </p>
                          {adj.status !== 'voided' && !adj.is_reversal && (
                            <div className="flex gap-2 mt-2 justify-end">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditModal(adj)}
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openVoidModal(adj)}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                Void
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Adjustments Pagination */}
            {Math.ceil(adjustmentsTotal / 20) > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustmentsPage(p => Math.max(1, p - 1))}
                  disabled={adjustmentsPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {adjustmentsPage} of {Math.ceil(adjustmentsTotal / 20)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustmentsPage(p => Math.min(Math.ceil(adjustmentsTotal / 20), p + 1))}
                  disabled={adjustmentsPage >= Math.ceil(adjustmentsTotal / 20)}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Adjustment Modal */}
        <Dialog open={createAdjustmentOpen} onOpenChange={setCreateAdjustmentOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Balance Adjustment</DialogTitle>
              <DialogDescription>
                Deduct or credit Milli's from a user's balance. The user will be notified.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* User Selection */}
              <div className="space-y-2">
                <Label>Select User *</Label>
                <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={userDropdownOpen}
                      className="w-full justify-between font-normal"
                    >
                      {selectedUserName || "Select user..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Search by name or phone..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="max-h-[250px] overflow-y-auto">
                      {filteredUsers.slice(0, 50).map(u => (
                        <div
                          key={u.user_id}
                          className="flex items-center px-2 py-2 cursor-pointer hover:bg-accent"
                          onClick={() => {
                            setSelectedUserId(u.user_id);
                            setSelectedUserName(`${u.name} (${u.phone})`);
                            setUserSearchTerm('');
                            setUserDropdownOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedUserId === u.user_id ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="flex flex-col">
                            <span className="text-sm">{u.name}</span>
                            <span className="text-xs text-muted-foreground">{u.phone} • Balance: {u.milli_balance?.toFixed(2) || '0.00'} Milli's</span>
                          </div>
                        </div>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                          No users found
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Adjustment Type */}
              <div className="space-y-2">
                <Label>Adjustment Type *</Label>
                <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-red-600" />
                        Deduction (Remove Milli's)
                      </div>
                    </SelectItem>
                    <SelectItem value="credit">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-teal-600" />
                        Credit (Add Milli's)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount (Milli's) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="Enter amount in Milli's"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(e.target.value)}
                />
              </div>

              {/* Remark */}
              <div className="space-y-2">
                <Label>Remark * <span className="text-xs text-muted-foreground">(min 5 characters)</span></Label>
                <Textarea
                  placeholder="Enter reason for adjustment (e.g., Policy violation - unauthorized referral abuse)"
                  value={adjustmentRemark}
                  onChange={(e) => setAdjustmentRemark(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateAdjustmentOpen(false); resetCreateForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAdjustment} 
                disabled={createAdjustmentLoading}
                className={adjustmentType === 'debit' ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'}
              >
                {createAdjustmentLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {adjustmentType === 'debit' ? 'Deduct Balance' : 'Credit Balance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Adjustment Modal */}
        <Dialog open={editAdjustmentOpen} onOpenChange={setEditAdjustmentOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Adjustment</DialogTitle>
              <DialogDescription>
                Modify the adjustment amount or remark. User balance will be updated accordingly.
              </DialogDescription>
            </DialogHeader>
            {editingAdjustment && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm"><span className="font-medium">User:</span> {editingAdjustment.user_name}</p>
                  <p className="text-sm"><span className="font-medium">Type:</span> {editingAdjustment.adjustment_type === 'debit' ? 'Deduction' : 'Credit'}</p>
                  <p className="text-sm"><span className="font-medium">Current Amount:</span> {editingAdjustment.milli_amount?.toFixed(2)} Milli's</p>
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label>New Amount (Milli's)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Enter new amount"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />
                </div>

                {/* Remark */}
                <div className="space-y-2">
                  <Label>Remark</Label>
                  <Textarea
                    placeholder="Update remark"
                    value={editRemark}
                    onChange={(e) => setEditRemark(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAdjustmentOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditAdjustment} 
                disabled={editAdjustmentLoading}
              >
                {editAdjustmentLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Void Adjustment Modal */}
        <Dialog open={voidAdjustmentOpen} onOpenChange={setVoidAdjustmentOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Void Adjustment</DialogTitle>
              <DialogDescription>
                This will void the adjustment and create a reversal transaction to restore the user's balance.
              </DialogDescription>
            </DialogHeader>
            {voidingAdjustment && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm"><span className="font-medium">User:</span> {voidingAdjustment.user_name}</p>
                  <p className="text-sm"><span className="font-medium">Type:</span> {voidingAdjustment.adjustment_type === 'debit' ? 'Deduction' : 'Credit'}</p>
                  <p className="text-sm"><span className="font-medium">Amount:</span> {voidingAdjustment.milli_amount?.toFixed(2)} Milli's</p>
                  <p className="text-sm"><span className="font-medium">Original Remark:</span> {voidingAdjustment.remark}</p>
                </div>

                {/* Void Reason */}
                <div className="space-y-2">
                  <Label>Reason for Voiding * <span className="text-xs text-muted-foreground">(min 5 characters)</span></Label>
                  <Textarea
                    placeholder="Enter reason for voiding this adjustment"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidAdjustmentOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleVoidAdjustment} 
                disabled={voidAdjustmentLoading}
                variant="destructive"
              >
                {voidAdjustmentLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Void Adjustment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminTransactionsPage;
